import type { DemoContent } from '@/types/content'

export const initialDemoContent: DemoContent = {
  version: '0.2.0',
  title: '메타 인지\n수학 테스트',
  subtitle: '당신의 수학 뇌는 어떤 유형일까요?',

  pretest: [
    {
      id: 'pre-01',
      enabled: true,
      section: 'pretest',
      type: 'PRE',
      prompt: '밑변이 5, 높이가 2인\n삼각형의 넓이는?',
      imageSrc: '/assets/problems/pre-01.png',
      timerSeconds: 10,
      timeoutChoiceId: '**last**',
      correctChoiceId: '5',
      feedback: {
        correctText: '상위 문제로 이동합니다!',
        incorrectText: '하위 문제로 이동합니다!',
      },
      choices: [
        { id: '5', text: '5' },
        { id: '10', text: '10' },
        { id: 'unknown', text: '모르겠습니다' },
      ],
    },

    {
      id: 'pre-02',
      enabled: true,
      section: 'pretest',
      type: 'PRE',
      prompt: "함수 f(x) = x²의\n도함수 f'(x)는?",
      timerSeconds: 10,
      timeoutChoiceId: '**last**',
      correctChoiceId: '2x',
      feedback: {
        correctText: '상위 문제로 이동합니다!',
        incorrectText: '하위 문제로 이동합니다!',
      },
      choices: [
        { id: 'x/2', text: 'x/2' },
        { id: '2x', text: '2x' },
        { id: 'unknown', text: '모르겠습니다' },
      ],
    },

    {
      id: 'pre-03',
      enabled: true,
      section: 'pretest',
      type: 'PRE',
      prompt: '좌표평면의 원점(0,0)에서\n(4,3)까지의 거리는?',
      imageSrc: '/assets/problems/pre-03.png',
      timerSeconds: 10,
      timeoutChoiceId: '**last**',
      correctChoiceId: '5',
      choices: [
        { id: '6', text: '6' },
        { id: '5', text: '5' },
        { id: 'unknown', text: '모르겠습니다' },
      ],
    },
  ],

  mainTest: [
    {
      id: 'main-a-01',
      enabled: true,
      section: 'main',
      type: 'A',
      prompt: '15 × 15 = ?',
      instruction:
        '문제를 보고 10초 동안 생각한 뒤,\n자신이 떠올린 풀이 방법을 선택하세요.',
      timerSeconds: 10,
      thinkSeconds: 10,
      answerSeconds: 10,
      timeoutChoiceId: '**last**',

      choices: [
        {
          id: 'vertical',
          text: '세로셈 방식으로 계산',
          imageSrc: '/assets/problems/main-a-01-choice-01.png',
        },
        {
          id: 'double',
          text: '30×30을 2로 두 번 나눔',
          imageSrc: '/assets/problems/main-a-01-choice-02.png',
        },
        {
          id: 'square',
          text: '(10 + 5)² 또는 (20 − 5)²로 계산',
        },
        {
          id: 'unknown',
          text: '모르겠습니다',
        },
      ],
    },

    {
      id: 'main-a-02',
      enabled: true,
      section: 'main',
      type: 'A',
      prompt: '다음 네 수 중에서 가장 큰 수는?',
      instruction:
        '문제를 보고 10초 동안 생각한 뒤,\n자신이 떠올린 풀이 방법을 선택하세요.',
      imageSrc: '/assets/problems/main-a-02.png',
      timerSeconds: 10,
      thinkSeconds: 10,
      answerSeconds: 10,
      timeoutChoiceId: '**last**',

      choices: [
        {
          id: 'decimal',
          text: '모든 수를 소수로 변환하여 비교',
        },
        {
          id: 'pairwise',
          text: '소수끼리, 분수끼리 각각 비교 후\n승자끼리 비교',
        },
        {
          id: 'unknown',
          text: '모르겠습니다',
        },
      ],
    },

    {
      id: 'main-a-03',
      enabled: true,
      section: 'main',
      type: 'A',
      prompt:
        '왼쪽 파란 영역의 넓이가 10일 때,\n오른쪽 그림의 파란 영역의 넓이는?',
      instruction:
        '문제를 보고 10초 동안 생각한 뒤,\n자신이 떠올린 풀이 방법을 선택하세요.',

      imageSrc: '/assets/problems/main-a-03.png',

      timerSeconds: 10,
      thinkSeconds: 10,
      answerSeconds: 10,
      timeoutChoiceId: '**last**',

      choices: [
        {
          id: 'equation',
          text: '원의 반지름을 r로 놓고\n방정식을 세운 후 풀기',
        },
        {
          id: 'similarity',
          text: '도형의 닮음과 비례식을 사용해 풀기',
          imageSrc: '/assets/problems/main-a-03-choice-02.png',
        },
        {
          id: 'unknown',
          text: '모르겠습니다',
        },
      ],
    },

    {
      id: 'main-b-01',
      enabled: true,
      section: 'main',
      type: 'B',
      prompt: '11 ÷ 1.2 = ?',
      instruction: '이 문제를 어떻게 풀 수 있을지\n한 번 생각해 보세요.',
      visualType: 'division-error',
      timerSeconds: 20,
      thinkSeconds: 20,
      answerSeconds: 20,
      timeoutChoiceId: '**last**',

      choices: [
        { id: 'A', text: 'A' },
        { id: 'B', text: 'B' },
        { id: 'C', text: 'C' },
        { id: 'none', text: '오류\n없음' },
      ],
    },

    {
      id: 'main-b-02',
      enabled: true,
      section: 'main',
      type: 'B',
      prompt: '',
      imageSrc: '/assets/problems/main-b-02.png',
      instruction: '이 문제를 어떻게 풀 수 있을지\n한 번 생각해 보세요.',
      visualType: 'operation-order-error',
      timerSeconds: 30,
      thinkSeconds: 30,
      answerSeconds: 30,
      timeoutChoiceId: '**last**',
      choices: [
        { id: 'A', text: 'A' },
        { id: 'B', text: 'B' },
        { id: 'C', text: 'C' },
        { id: 'none', text: '오류\n없음' },
      ],
    },

    {
      id: 'main-c-01',
      enabled: true,
      section: 'main',
      type: 'C',
      prompt: '직각삼각형의\n넓이를 구하시오.',
      instruction: '문제 오류 여부를 판단하세요.',
      imageSrc: '/assets/problems/main-c-01.png',
      timerSeconds: 30,
      timeoutChoiceId: '**last**',

      choices: [
        { id: 'no-error', text: '오류가 없습니다' },
        { id: 'error', text: '오류가 있습니다' },
        { id: 'unknown', text: '모르겠습니다' },
      ],
    },

    {
      id: 'main-c-02',
      enabled: true,
      section: 'main',
      type: 'C',
      prompt: '네 변의 길이가 순서대로\n4, 5, 6, 7인 사각형의\n넓이를 구하시오.',
      instruction: '문제 오류 여부를 판단하세요.',
      visualType: 'quadrilateral-conditions',
      timerSeconds: 30,
      timeoutChoiceId: '**last**',

      choices: [
        { id: 'no-error', text: '오류가 없습니다' },
        { id: 'error', text: '오류가 있습니다' },
        { id: 'unknown', text: '모르겠습니다' },
      ],
    },
  ],
}